import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, Loader2, Play } from "lucide-react";
import { StudentMobileShell } from "../components/student/StudentMobileShell";
import {
  COURSE_CATEGORIES,
  sortLectureItems,
} from "../lib/course-categories";
import { getAllScores, type ScoresResponse } from "../lib/scores-api";
import {
  buildCourseProgress,
  getCompletedScenarioIds,
  getLectureNumber,
  getScenarioBestScores,
  type StudentScenario,
  type StudentScenarioResponse,
} from "../lib/student-course-progress";

const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
  cyan: "#0E7490",
  cyanSoft: "#ECFEFF",
  green: "#15803D",
  greenSoft: "#DCFCE7",
  orange: "#FF6B1A",
  orangeSoft: "#FFF7ED",
};

function scenarioDescription(scenario: StudentScenario): string {
  return (
    scenario.summary?.trim() ||
    scenario.tasks[0]?.title ||
    "患者情報を確認して面接を始めます。"
  );
}

export default function StudentLearning() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const initialCategoryId = Number(
    new URLSearchParams(search).get("courseCategoryId"),
  );
  const [selectedCourseCategoryId, setSelectedCourseCategoryId] = useState(
    Number.isInteger(initialCategoryId) &&
      initialCategoryId >= 1 &&
      initialCategoryId <= COURSE_CATEGORIES.length
      ? initialCategoryId
      : 1,
  );
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);

  useEffect(() => {
    const requestedCategoryId = Number(
      new URLSearchParams(search).get("courseCategoryId"),
    );
    if (
      Number.isInteger(requestedCategoryId) &&
      requestedCategoryId >= 1 &&
      requestedCategoryId <= COURSE_CATEGORIES.length
    ) {
      setSelectedCourseCategoryId(requestedCategoryId);
    }
  }, [search]);

  const {
    data: scenarioData,
    isLoading: scenariosLoading,
    error: scenariosError,
  } = useQuery<StudentScenarioResponse>({
    queryKey: ["/api/scenarios"],
    staleTime: 30_000,
  });
  const { data: scoreData } = useQuery<ScoresResponse>({
    queryKey: ["student-mobile", "scores", "all"],
    queryFn: getAllScores,
    staleTime: 60_000,
    refetchOnMount: "always",
  });
  const categoryProgress = useMemo(
    () => buildCourseProgress(scenarioData, scoreData?.scores),
    [scenarioData, scoreData],
  );
  const completedScenarioIds = useMemo(
    () => getCompletedScenarioIds(scenarioData, scoreData?.scores),
    [scenarioData, scoreData],
  );
  const scenarioBestScores = useMemo(
    () => getScenarioBestScores(scenarioData, scoreData?.scores),
    [scenarioData, scoreData],
  );
  const courseScenarios = useMemo(() => {
    return sortLectureItems(
      (scenarioData?.scenarios ?? []).filter(
        (scenario) =>
          scenario.courseCategoryId === selectedCourseCategoryId,
      ).map((scenario) => ({
        ...scenario,
        courseCategoryId: scenario.courseCategoryId ?? null,
      })),
    );
  }, [scenarioData, selectedCourseCategoryId]);
  const selectedCategory =
    COURSE_CATEGORIES.find(
      (category) => category.id === selectedCourseCategoryId,
    ) ?? COURSE_CATEGORIES[0];
  const selectedProgress = categoryProgress.find(
    (item) => item.courseCategoryId === selectedCourseCategoryId,
  );

  return (
    <StudentMobileShell active="learning" eyebrow="LEARNING" title="学習">
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.16em",
            color: COLORS.muted,
          }}
        >
          LECTURE SELECT
        </div>
        <h1 style={{ margin: "6px 0", fontSize: 28, fontWeight: 900 }}>
          講座を選ぶ
        </h1>
        <p
          style={{
            margin: 0,
            color: COLORS.muted,
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          カテゴリを選び、取り組む講座を選択します。
        </p>
      </div>

      <section
        style={{
          marginBottom: 18,
          background: COLORS.surface,
          border: `1px solid ${COLORS.line}`,
          borderRadius: 12,
          padding: 18,
        }}
      >
        <div
          id="student-category-label"
          style={{
            display: "block",
            margin: "0 0 7px",
            fontSize: 12,
            fontWeight: 900,
            color: COLORS.muted,
          }}
        >
          カテゴリ
        </div>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            aria-labelledby="student-category-label"
            aria-haspopup="listbox"
            aria-expanded={categoryMenuOpen}
            onClick={() => setCategoryMenuOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setCategoryMenuOpen(false);
            }}
            style={{
              width: "100%",
              minHeight: 48,
              padding: "0 12px 0 14px",
              border: `2px solid ${COLORS.cyan}`,
              borderRadius: 10,
              background: COLORS.cyanSoft,
              color: COLORS.ink,
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <span style={{ minWidth: 0, lineHeight: 1.45 }}>
              {selectedCategory.id}．{selectedCategory.name}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
                color: COLORS.cyan,
                fontSize: 12,
              }}
            >
              {selectedProgress?.total
                ? `${selectedProgress.percent}%`
                : "未準備"}
              <ChevronDown
                size={18}
                style={{
                  transform: categoryMenuOpen ? "rotate(180deg)" : "none",
                }}
              />
            </span>
          </button>

          {categoryMenuOpen && (
            <div
              role="listbox"
              aria-labelledby="student-category-label"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                right: 0,
                zIndex: 30,
                maxHeight: 188,
                overflowY: "auto",
                padding: 5,
                border: `1px solid ${COLORS.line}`,
                borderRadius: 10,
                background: COLORS.surface,
                boxShadow: "0 12px 30px rgba(24, 24, 27, 0.16)",
              }}
            >
              {COURSE_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  role="option"
                  aria-selected={category.id === selectedCourseCategoryId}
                  onClick={() => {
                    setSelectedCourseCategoryId(category.id);
                    setLocation(
                      `/mypage/learning?courseCategoryId=${category.id}`,
                      { replace: true },
                    );
                    setCategoryMenuOpen(false);
                  }}
                  style={{
                    width: "100%",
                    minHeight: 40,
                    padding: "9px 10px",
                    border: 0,
                    borderRadius: 7,
                    background:
                      category.id === selectedCourseCategoryId
                        ? COLORS.cyanSoft
                        : "transparent",
                    color: COLORS.ink,
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight:
                      category.id === selectedCourseCategoryId ? 900 : 700,
                    lineHeight: 1.45,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <span>
                      {category.id}．{category.name}
                    </span>
                    <span style={{ color: COLORS.cyan, flexShrink: 0 }}>
                      {categoryProgress.find(
                        (item) => item.courseCategoryId === category.id,
                      )?.total
                        ? `${categoryProgress.find(
                            (item) => item.courseCategoryId === category.id,
                          )?.percent ?? 0}%`
                        : "未準備"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <div
        style={{
          marginBottom: 10,
          fontSize: 13,
          fontWeight: 900,
          color: COLORS.muted,
        }}
      >
        {courseScenarios.length}件の講座
      </div>

      {scenariosLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: 24,
            color: COLORS.muted,
          }}
        >
          <Loader2 className="animate-spin" size={20} />
          講座を読み込み中...
        </div>
      ) : scenariosError ? (
        <div
          style={{
            padding: 18,
            borderRadius: 10,
            background: COLORS.orangeSoft,
            color: COLORS.orange,
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          講座を読み込めませんでした。
        </div>
      ) : courseScenarios.length === 0 ? (
        <div
          style={{
            padding: 22,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            background: COLORS.surface,
            color: COLORS.muted,
            fontSize: 13,
            fontWeight: 800,
            lineHeight: 1.7,
          }}
        >
          このカテゴリには、管理画面で承認された課題文がまだありません。
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          {courseScenarios.map((scenario) => {
            const isCompleted = completedScenarioIds.has(scenario.id);
            const bestScore = scenarioBestScores.get(scenario.id);
            return (
              <article
                key={scenario.id}
                style={{
                  background: isCompleted ? COLORS.greenSoft : COLORS.surface,
                  border: `1px solid ${isCompleted ? COLORS.green : COLORS.line}`,
                  borderRadius: 12,
                  padding: 18,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div>
                    <h2
                      style={{
                        margin: "0 0 6px",
                        fontSize: 19,
                        fontWeight: 900,
                      }}
                    >
                      {scenario.lectureTitle?.trim() ||
                        `講座 ${getLectureNumber(scenarioData, scenario.id) ?? "-"}`}
                    </h2>
                  </div>
                  {isCompleted ? (
                    <div
                      style={{
                        flex: "0 0 auto",
                        minWidth: 116,
                        padding: "8px 10px",
                        border: `1px solid ${COLORS.green}`,
                        borderRadius: 999,
                        background: COLORS.surface,
                        color: COLORS.green,
                        display: "grid",
                        gridTemplateColumns: "18px 1fr",
                        alignItems: "center",
                        columnGap: 6,
                        fontWeight: 900,
                      }}
                    >
                      <CheckCircle2 size={18} />
                      <div>
                        <div style={{ fontSize: 12, lineHeight: 1.2 }}>
                          A判定・クリア済み
                        </div>
                        {bestScore !== undefined && (
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 10.5,
                              lineHeight: 1.2,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            最高 {bestScore}点
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span
                      style={{
                        flex: "0 0 auto",
                        padding: "6px 10px",
                        borderRadius: 999,
                        background: COLORS.surface,
                        color: COLORS.muted,
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      {bestScore !== undefined ? "未クリア" : "未受講"}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    margin: "2px 0 10px",
                    color: isCompleted ? COLORS.green : COLORS.muted,
                    fontSize: 12,
                    fontWeight: 900,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {isCompleted
                    ? "A判定を取得済みです。繰り返し練習できます。"
                    : bestScore !== undefined
                      ? `現在の最高得点 ${bestScore}点・患者面接と報告の総合80点以上でクリア`
                      : "未受講・患者面接と報告の総合80点以上でクリア"}
                </div>
                <p
                  style={{
                    margin: "8px 0 16px",
                    color: COLORS.muted,
                    fontSize: 13,
                    lineHeight: 1.75,
                  }}
                >
                  {scenarioDescription(scenario)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    window.sessionStorage.setItem(
                      "student-task-briefing",
                      JSON.stringify({
                        instructionId: scenario.id,
                        categoryName: selectedCategory.name,
                        patientName: "患者",
                        taskTitle: scenarioDescription(scenario),
                        taskText: scenarioDescription(scenario),
                        questionnaireText: null,
                      }),
                    );
                    setLocation(
                      `/mypage/task-briefing?instructionId=${scenario.id}`,
                    );
                  }}
                  style={{
                    width: "100%",
                    minHeight: 46,
                    border: 0,
                    borderRadius: 10,
                    background: COLORS.orange,
                    color: "#FFFFFF",
                    fontSize: 14,
                    fontWeight: 900,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <Play size={17} fill="currentColor" />
                  この講座で面接を始める
                </button>
              </article>
            );
          })}
        </div>
      )}
    </StudentMobileShell>
  );
}
